import {
  fetchHouseIdentity,
  fetchLotPhotos,
  fetchMySaleStandings,
  houseLabel,
  type SaleStanding,
} from '@auctisync/api-client';
import type { Tables } from '@auctisync/shared';
import { useEffect, useMemo, useState } from 'react';
import { SaleTermsModal } from '../../components/SaleTermsModal';
import { Link } from 'react-router-dom';
import { Countdown } from '../../components/Countdown';
import { EmptyState } from '../../components/bidder-web';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useConnection } from '../../hooks/useConnection';
import { useServerClock } from '../../hooks/useServerClock';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';
import { useFollow } from '../../hooks/useFollow';
import { useCatalogNav } from '../preview/CatalogNav';
import { auctionPath } from '../preview/previewNav';

type Auction = Pick<
  Tables<'auctions'>,
  | 'id'
  | 'slug'
  | 'title'
  | 'description'
  | 'preview_text'
  | 'status'
  | 'starts_at'
  | 'ends_at'
  | 'lot_count'
  | 'tenant_id'
  | 'pickup_location'
  | 'pickup_city'
  | 'pickup_region'
  // Everything below is read only by the sale-terms modal this list opens.
  // Fifteen scalars on a page of sales is cheaper than a second round trip
  // when someone taps "Sale terms".
  | 'buyers_premium_bps'
  | 'online_premium_bps'
  | 'requires_terms_acceptance'
  | 'requires_bidder_approval'
  | 'requires_cc_preauth'
  | 'cc_preauth_amount_cents'
  | 'extension_threshold_seconds'
  | 'extension_amount_seconds'
  | 'preview_starts_at'
  | 'preview_ends_at'
  | 'pickup_address_line1'
  | 'pickup_address_line2'
  | 'pickup_postal_code'
  | 'pickup_instructions'
  | 'pickup_dates'
  | 'timezone'
>;

const AUCTION_COLUMNS =
  // ONE string literal, deliberately: the typed client infers the row shape
  // from the literal, and a concatenated string degrades it to GenericStringError.
  // The tail (from `buyers_premium_bps`) is read only by the sale-terms modal.
  'id, slug, title, description, preview_text, status, starts_at, ends_at, lot_count, tenant_id, pickup_location, pickup_city, pickup_region, buyers_premium_bps, online_premium_bps, requires_terms_acceptance, requires_bidder_approval, requires_cc_preauth, cc_preauth_amount_cents, extension_threshold_seconds, extension_amount_seconds, preview_starts_at, preview_ends_at, pickup_address_line1, pickup_address_line2, pickup_postal_code, pickup_instructions, pickup_dates, timezone';

/** Open sales a bidder can still act on, versus finished ones. */
const OPEN_STATUSES = ['preview', 'active'];

type Tab = 'current' | 'past';

/**
 * Sales — every sale this house has run, open and closed.
 *
 * REBUILT to the web design handoff (`web/web-pages.jsx`, 2026-09-02). What it
 * replaced was a four-way status filter (`all / active / preview / completed`)
 * over a card grid, which asked the bidder to know the difference between
 * "preview" and "active" — an auctioneer's vocabulary, on the page a buyer
 * uses. Two tabs answer the only question a bidder is actually asking: can I
 * still bid on this, or is it over?
 *
 * 🔴 AN ENDED SALE NEVER ROUTES INTO LIVE BROWSE. There is nothing there to bid
 * on, and a "Browse lots" button on a finished sale is a promise the next page
 * cannot keep. It shows results — and, for a bidder who won something, their
 * invoice, which is the thing they actually came back for.
 */
export function PublicAuctionList() {
  const { nowServerMs } = useServerClock();
  const { userId } = useSession();
  const { state: connection } = useConnection();
  // On the shared app a signed-in bidder is "in" one house at a time — scope the
  // catalog to it. Unauthenticated / no-house-selected shows all houses' sales.
  const { selected, active, discoverHouse } = useBidderHouses();
  // The onboarding preview renders this page under `/preview/<slug>`, so every
  // catalogue link is built from the base path rather than hardcoded.
  const { basePath, previewMode } = useCatalogNav();

  const [rows, setRows] = useState<Auction[]>([]);
  const [covers, setCovers] = useState<ReadonlyMap<string, string>>(new Map());
  const [standings, setStandings] = useState<ReadonlyMap<string, SaleStanding>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('current');
  const [termsFor, setTermsFor] = useState<Auction | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      let query = supabase
        .from('auctions')
        .select(AUCTION_COLUMNS)
        .eq('is_visible', true)
        .in('status', ['preview', 'active', 'completed'])
        .order('ends_at', { ascending: true, nullsFirst: false });
      if (selected) query = query.eq('tenant_id', selected.tenant_id);

      const { data, error: err } = await query;
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setLoading(false);
        return;
      }
      const sales = (data ?? []) as Auction[];
      setRows(sales);
      setLoading(false);

      const firstTenant = sales[0]?.tenant_id;
      if (!selected && firstTenant !== undefined) {
        // Only when every sale on screen belongs to ONE house. On the shared
        // domain a mixed list has no single owner, and branding the page for
        // whichever sale happened to sort first would be a lie about the rest.
        const single = sales.every((s) => s.tenant_id === firstTenant);
        if (single) {
          void fetchHouseIdentity(supabase, firstTenant).then((house) => {
            if (!cancelled) discoverHouse(house);
          });
        }
      }

      // A cover photograph per sale — the first lot's. Sales carry no image of
      // their own, and a photograph from inside the sale is both true and the
      // only one we have. One read for the whole page rather than one per row.
      const ids = sales.map((s) => s.id);
      if (ids.length > 0) {
        const { data: lotRows } = await supabase
          .from('lots')
          .select('id, auction_id, lot_number, inventory_item_id')
          .in('auction_id', ids)
          .eq('is_visible', true)
          .order('lot_number', { ascending: true })
          .limit(400);
        if (cancelled) return;
        const lots = (lotRows ?? []) as {
          id: string;
          auction_id: string;
          inventory_item_id: string | null;
        }[];
        const photos = await fetchLotPhotos(supabase, lots);
        if (cancelled) return;
        const byAuction = new Map<string, string>();
        for (const lot of lots) {
          if (byAuction.has(lot.auction_id)) continue;
          const url = photos.get(lot.id)?.[0]?.url;
          if (url !== undefined) byAuction.set(lot.auction_id, url);
        }
        setCovers(byAuction);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, discoverHouse]);

  /**
   * Where this bidder stands in each sale — one read, grouped, not one per row.
   *
   * RLS confines `bids` to the caller's own rows, so a rival's activity is
   * invisible here by construction rather than by our filtering.
   */
  useEffect(() => {
    if (userId === null) {
      setStandings(new Map());
      return;
    }
    let cancelled = false;
    // The rules — and the grouping by sale — are the shared client's, so the
    // phone's sale card and this one say the same thing about the same sale.
    void fetchMySaleStandings(supabase, userId, nowServerMs()).then(({ standings: mine }) => {
      if (!cancelled) setStandings(mine);
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const shown = useMemo(
    () =>
      rows.filter((r) =>
        tab === 'current' ? OPEN_STATUSES.includes(r.status) : r.status === 'completed',
      ),
    [rows, tab],
  );

  const houseName = active === null ? 'This platform' : houseLabel(active);

  return (
    <div className="w-wrap">
      <div style={{ paddingTop: 40 }}>
        <h1 className="w-h1">Auctions</h1>
        <p className="w-body muted" style={{ marginTop: 10, maxWidth: 560 }}>
          {active === null
            ? 'Every sale open right now, and the ones that have finished. Lots inside a sale close in staggered order, not all at once.'
            : `Every ${houseName} sale, open and closed. Lots inside a sale close in staggered order, not all at once.`}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '26px 0 22px' }} role="tablist">
        {(
          [
            ['current', 'Current'],
            ['past', 'Past'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            className="w-chip"
            aria-selected={tab === id}
            aria-pressed={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {error ? (
        <EmptyState icon="!" title="Sales could not be loaded" body={error} />
      ) : loading ? (
        <p className="muted">Loading sales…</p>
      ) : shown.length === 0 ? (
        <EmptyState
          icon={tab === 'current' ? '⏱' : '▤'}
          title={tab === 'current' ? 'No sale is open right now' : 'No past sales yet'}
          body={
            tab === 'current'
              ? 'When a sale opens it appears here, with a countdown to the first lot closing.'
              : 'Closed sales and their results appear here once a sale ends.'
          }
          action={
            tab === 'current' ? (
              <button type="button" className="w-btn w-btn-out" onClick={() => setTab('past')}>
                See past sales
              </button>
            ) : undefined
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {shown.map((sale) => (
            <SaleRow
              key={sale.id}
              sale={sale}
              coverUrl={covers.get(sale.id) ?? null}
              standing={standings.get(sale.id) ?? null}
              nowServerMs={nowServerMs}
              offline={connection === 'offline'}
              to={auctionPath(basePath, sale.slug)}
              browseTo={previewMode ? null : `/browse?sale=${sale.slug}`}
              onTerms={() => {
                setTermsFor(sale);
              }}
            />
          ))}
        </div>
      )}

      {/* ONE modal for the list, not one per row: it is a single overlay and a
          row does not own it. */}
      {termsFor !== null ? (
        <SaleTermsModal
          auction={termsFor}
          houseName={houseName}
          onClose={() => {
            setTermsFor(null);
          }}
          browseTo={
            OPEN_STATUSES.includes(termsFor.status) && !previewMode
              ? `/browse?sale=${termsFor.slug}`
              : null
          }
        />
      ) : null}
    </div>
  );
}

function SaleRow({
  sale,
  coverUrl,
  standing,
  nowServerMs,
  offline,
  to,
  browseTo,
  onTerms,
}: {
  sale: Auction;
  coverUrl: string | null;
  standing: SaleStanding | null;
  nowServerMs: () => number;
  offline: boolean;
  to: string;
  /** Null inside the onboarding preview, which has no `/browse` route. */
  browseTo: string | null;
  onTerms: () => void;
}) {
  const open = OPEN_STATUSES.includes(sale.status);
  const where =
    [sale.pickup_location, [sale.pickup_city, sale.pickup_region].filter(Boolean).join(', ')]
      .filter((part) => part !== null && part !== '')
      .join(' · ') || null;

  return (
    <div className="w-sale">
      {/* An open sale row goes straight to its lots — the results page would
          only redirect there. Ended sales go to their results. */}
      <Link to={open ? (browseTo ?? to) : to} className="ph" aria-label={sale.title}>
        {coverUrl === null ? <em>no photograph</em> : <img src={coverUrl} alt="" loading="lazy" />}
      </Link>

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            marginBottom: 8,
            flexWrap: 'wrap',
          }}
        >
          {open ? (
            offline ? (
              <span className="w-pill">Not updating</span>
            ) : (
              <span className="w-pill">
                <span className="w-dot" />
                {sale.status === 'active' ? 'Live' : 'Preview'}
              </span>
            )
          ) : (
            <span className="w-lbl">Ended</span>
          )}
          {standing !== null && standing.winning > 0 ? (
            <span className="tag t-win">Winning {standing.winning} in this sale</span>
          ) : null}
          {standing !== null && standing.won > 0 ? (
            <span className="tag t-win">You won {standing.won}</span>
          ) : null}
        </div>

        <h2 className="w-h2" style={{ fontSize: 21 }}>
          <Link
            to={open ? (browseTo ?? to) : to}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {sale.title}
          </Link>
        </h2>
        {(sale.preview_text ?? sale.description) ? (
          <p className="w-body muted" style={{ fontSize: 14, marginTop: 6 }}>
            {sale.preview_text ?? sale.description}
          </p>
        ) : null}

        <div className="w-sale-m" style={{ marginTop: 14 }}>
          <div>
            <span className="w-lbl">Lots</span>
            <b>{sale.lot_count}</b>
          </div>
          <div>
            <span className="w-lbl">{open ? 'Closes' : 'Closed'}</span>
            <b style={{ fontSize: 15 }}>
              {sale.ends_at === null ? (
                'Not scheduled'
              ) : open && !offline ? (
                <Countdown endsAt={sale.ends_at} nowServerMs={nowServerMs} />
              ) : (
                new Date(sale.ends_at).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              )}
            </b>
          </div>
          {where !== null ? (
            <div>
              <span className="w-lbl">Pickup</span>
              <b style={{ fontSize: 15 }}>{where}</b>
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, alignItems: 'stretch' }}>
        {open ? (
          <Link to={browseTo ?? to} className="w-btn w-btn-cta">
            Browse lots<span aria-hidden>›</span>
          </Link>
        ) : standing !== null && standing.won > 0 ? (
          <Link to="/my/invoices" className="w-btn w-btn-cta">
            View your invoice<span aria-hidden>›</span>
          </Link>
        ) : (
          <Link to={to} className="w-btn w-btn-cta">
            View results<span aria-hidden>›</span>
          </Link>
        )}
        {/* Terms is reachable from every row, open OR ended: an ended sale's
            terms are the terms its invoices were struck under. It opens a modal
            rather than navigating, because the content is short and a bidder
            scanning a list should not lose the list to read it. */}
        <button type="button" className="w-btn w-btn-out w-btn-sm" onClick={onTerms}>
          Sale terms
        </button>
        {/* Following a SALE (Module #23) lived only on the auction detail page.
            That page is results-only now and an open sale never reaches it, so
            without this the feature would have had no route at all. The row is
            the better home anyway: following is a decision made while scanning
            sales, not after opening one. Ended sales cannot be followed —
            there is nothing left to be notified about. */}
        {open ? <SaleFollowButton auctionId={sale.id} /> : null}
      </div>
    </div>
  );
}

/**
 * Follow a sale, in the bidder surface's own button vocabulary.
 *
 * The LOGIC is `useFollow`, shared with the lot page and the admin surfaces —
 * following is one rule and lives once. Only the presentation differs, because
 * `FollowButton` renders a shadcn control that would sit in a redesigned sale
 * row at the wrong height, radius and type. See
 * `memory/feedback_no_mechanical_divergence.md`: what must not diverge is the
 * rule, not the paint.
 */
function SaleFollowButton({ auctionId }: { auctionId: string }) {
  const { session } = useSession();
  const { ready, following, pending, toggle } = useFollow('auction', auctionId);

  if (!session) {
    return (
      <Link to="/signin" className="w-btn w-btn-out w-btn-sm">
        Sign in to follow
      </Link>
    );
  }

  return (
    <button
      type="button"
      className="w-btn w-btn-out w-btn-sm"
      aria-pressed={following}
      disabled={!ready || pending}
      onClick={() => {
        void toggle();
      }}
    >
      {pending ? '…' : following ? '★ Following' : '☆ Follow this sale'}
    </button>
  );
}
